import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { debounce } from "capitalrx-components-2.0";
import { DataId, Filterable, Icon, useAlert, useRemoteTableDataSource, useSimpleTableDataSource } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { isZipCode } from "capitalrx-components-2.0/lib/components/FormElements/validation";
import { Form, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Heading } from "capitalrx-components-2.0/lib/typography";

import { getGeolocation } from "@/api/axios/geolocation";
import { Drug } from "@/api/types";
import { TypeaheadTextbox } from "@/common/components/TypeaheadTextbox";
import { TypeaheadTextboxDatasourceRow } from "@/common/components/TypeaheadTextbox/Props";
import { useNavigateSearch, useSelectedEnrollmentPlan } from "@/premium/hooks";
import { Breadcrumbs, Card, NeedHelp, PageLayout, ThemedButton, ThemedSubmitButton, ThemedTitle } from "@/premium/themedComponents";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";

import { DrugDetailsForm } from "./DrugDetailsForm";


interface FindPharmaciesForm {
  readonly zipCode: string;
  readonly latitude?: number;
  readonly longitude?: number;
  readonly pharmacyName?: string;
  readonly drug?: {
    readonly drugQuantity: string;
    readonly drugDisplayQuantity: string;
    readonly drugDosage: string;
    readonly drugForm: string;
    readonly claimRunDate?: string;
    readonly daysSupply: string;
  };
  readonly member?: {
    readonly gender?: string;
    readonly age?: string;
  };
}

const drugDebounceFilter = debounce((dataSource: Filterable, value: string | null) => {
  if (!value) {
    dataSource.clearFilter();
  } else {
    dataSource.filterBy("search", value);
  }
}, 300);

export const FindPharmacies: FC = () => {
  const { t: translate } = useTranslation();
  const navigate = useNavigateSearch();
  const [searchParams] = useSearchParams();
  const enrollmentPlanId = searchParams.get("enrollmentPlan");
  const { enrollmentPlanCsid, hideDrugPricing, hidePharmacySearch } = useSelectedEnrollmentPlan();
  const [selectedDrug, setSelectedDrug] = useState<Drug>(undefined);
  const [drugName, setDrugName] = useState<string>("");
  const alert = useAlert();

  useEffect(() => {
    if (hidePharmacySearch) {
      navigate("/");
    }
  }, [hidePharmacySearch]);

  const { columns } = useResponsive({
    columns: {
      desktop: 3,
      tablet: 6,
      mobile: 12,
    },
  });

  const defaultValues: FindPharmaciesForm = useMemo(() => {
    return {
      zipCode: "",
    };
  }, []);

  const pageTitle = <ThemedTitle>{translate("FIND_PHARMACIES.findPharmacies.findPharmacies")}{!hideDrugPricing ? ` ${translate("FIND_PHARMACIES.findPharmacies.andDrugPricing")}` : ""}</ThemedTitle>;

  const findPharmaciesForm = useForm<FindPharmaciesForm>({
    defaultValues: defaultValues,
    shouldFocusError: false,
  });
  const location = findPharmaciesForm.watch("zipCode");
  const pharmacyName = findPharmaciesForm.watch("pharmacyName");

  const currentLocationRows = useMemo(() => {
    return [
      {
        id: "zipCode",
        data: {
          displayText: translate("FIND_PHARMACIES.findPharmacies.currentLocation"),
          value: "Current Location",
          data: {},
        },
      },
    ];
  }, [translate]);

  const currentLocationDataSource = useSimpleTableDataSource(currentLocationRows);

  useEffect(() => {
    if (location === "Current Location") {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          findPharmaciesForm.setValue("latitude", position.coords.latitude);
          findPharmaciesForm.setValue("longitude", position.coords.longitude);
          findPharmaciesForm.setValue("zipCode", "Current Location");
        },
        () => {
          alert({
            message: translate("FIND_PHARMACIES.findPharmacies.cannotGetCurrentLocation"),
            type: "error",
            style: "toast",
          });
          findPharmaciesForm.setValue("zipCode", "");
        },
      );
    }
  }, [location]);

  const drugSearchDataSource = useRemoteTableDataSource<{ readonly drug: Drug }, TypeaheadTextboxDatasourceRow<any>>(
    `${site.baseUrl}/drug/list`,
    (row) => {
      return {
        displayText: row.drug.drug_name,
        value: row.drug.id,
        data: row.drug,
      };
    },
    (row) => row.value,
    {
      enabled: (drugName?.length || 0) >= 3,
      refetchInterval: 0,
      onError: () => {},
    },
  );

  useEffect(() => {
    drugDebounceFilter(drugSearchDataSource, drugName);
  }, [drugName]);

  const onDrugSelected = useCallback(
    (drugId: DataId) => {
      const row = drugSearchDataSource.lookup(drugId);
      if (row) {
        setSelectedDrug(row.data);
        const mostCommonDosage = row.data.most_common_dosage ?? row.data.configurations[0].dosage;
        const mostCommonForm = row.data.most_common_form ?? row.data.configurations[0].dosage_form;
        const mostCommonQuantity = row.data.most_common_quantity_dispensed ?? row.data.configurations[0].quantities[0];

        findPharmaciesForm.setValue("drug.drugForm", mostCommonForm);
        findPharmaciesForm.setValue("drug.drugDosage", mostCommonDosage);
        findPharmaciesForm.setValue("drug.drugQuantity", mostCommonQuantity.toString());
        findPharmaciesForm.setValue("drug.daysSupply", "30");

        const defaultRunDate = DateTime.max(
          DateTime.fromISO(enrollmentPlanCsid.claim_run_date, { zone: "utc" }),
          DateTime.utc(),
        );

        findPharmaciesForm.setValue(
          "drug.claimRunDate",
          defaultRunDate.toFormat("MM/dd/yyyy"),
        );

        findPharmaciesForm.setValue("member.gender", "Female");
        findPharmaciesForm.setValue("member.age", "44");
      }
    },
    [drugSearchDataSource, findPharmaciesForm],
  );

  const onFormSearchCancel = useCallback(() => {
    findPharmaciesForm.reset();
    setDrugName(undefined);
    setSelectedDrug(undefined);
  }, [location, pharmacyName]);

  const tryGeoLocateWithZipCode = async (zipCode: string) => {
    try {
      const location = await getGeolocation(zipCode);
      if (location?.latitude && location?.longitude) {
        return location;
      } else {
        return undefined;
      }
    } catch {
      return undefined;
    }
  };

  const onFindPharmacies = useCallback(
    async (formData: FindPharmaciesForm) => {
      let params: { [key: string]: string } = { enrollmentPlan: enrollmentPlanId };
      if (formData.pharmacyName) {
        params = { ...params, pharmacyName: formData.pharmacyName };
      }
      if (selectedDrug) {
        // figure out the real quantity
        const drugForm = formData.drug.drugForm;
        const drugDosage = formData.drug.drugDosage;
        const drugQuantityWithForm = formData.drug.drugQuantity;

        const drugConfiguration = selectedDrug.configurations.find((configuration) => {
          return configuration.dosage_form === drugForm && configuration.dosage === drugDosage;
        });
        const rawAmount = parseFloat(drugQuantityWithForm);
        const displayQuantityIdx = drugConfiguration.display_quantities.indexOf(String(rawAmount));
        const quantity = drugConfiguration.quantities[displayQuantityIdx] || rawAmount;

        // set the claim run date
        let claimRunDate = DateTime.fromISO(enrollmentPlanCsid.claim_run_date);
        if (formData.drug.claimRunDate) {
          claimRunDate = DateTime.fromFormat(formData.drug.claimRunDate, "MM/dd/yyyy").toUTC();
        }

        params = {
          ...params,
          age: formData.member?.age || "44",
          gender: formData.member?.gender || "Female",
          daysSupply: encodeURIComponent(formData.drug?.daysSupply || "30"),
          drugId: selectedDrug.id.toString(),
          dosageForm: encodeURIComponent(drugForm),
          dosage: encodeURIComponent(drugDosage),
          quantity: quantity.toString(),
          claimRunDate: claimRunDate.toISO(),
        };
      }
      if (formData.zipCode !== "Current Location") {
        const loc = await tryGeoLocateWithZipCode(formData.zipCode);
        if (loc) {
          params = { ...params, latitude: loc.latitude, longitude: loc.longitude, zipCode: formData.zipCode };
        } else {
          alert({
            message: `${translate("FIND_PHARMACIES.findPharmacies.unableToFindLocation")} ${formData.zipCode}.`,
            style: "toast",
            type: "error",
          });
          return;
        }
      } else {
        params = { ...params, latitude: formData.latitude.toString(), longitude: formData.longitude.toString() };
      }
      logEvent(ANALYTIC_EVENTS.FIND_PHARMACY);
      navigate("/plan/find-pharmacies/pharmacies", params);
    },
    [selectedDrug],
  );

  const zipOrCurrentLocationValidator = useCallback((value: string) => {
    if (value === "Current Location") {
      return undefined;
    } else {
      return isZipCode(value);
    }
  }, []);

  return (
    <PageLayout>
      <Form form={findPharmaciesForm} onSubmit={onFindPharmacies}>
        <Stack spacing={"medium-large"}>
          <Breadcrumbs />
          {pageTitle}
          <Card>
            <Stack spacing="large">
              <Stack>
                <Grid spacing="large">
                  <Grid.Span columns={columns}>
                    <Stack>
                      <Heading>{translate("FIND_PHARMACIES.findPharmacies.pharmacySearch")}</Heading>
                      <Stack spacing="none">
                        <TypeaheadTextbox.Field
                          testId="zip-code-field"
                          name="zipCode"
                          required
                          prefix={<Icon name="search" />}
                          label={translate("FIND_PHARMACIES.findPharmacies.enterZipCode")}
                          dataSource={currentLocationDataSource}
                          openOnFocus={true}
                          validator={zipOrCurrentLocationValidator}
                        />
                      </Stack>
                      <Textbox.Field
                        name="pharmacyName"
                        testId="pharmacy-name-field"
                        prefix={<Icon name="search" />}
                        label={translate("FIND_PHARMACIES.findPharmacies.searchForPharmacyByName")}
                      />
                    </Stack>
                  </Grid.Span>
                  <Grid.Break />
                  {!hideDrugPricing && (
                    <Grid.Span columns={columns}>
                      <Stack>
                        <Heading>{translate("FIND_PHARMACIES.findPharmacies.drugPricingSearch")}</Heading>
                        <Stack spacing="none">
                          <TypeaheadTextbox
                            label={translate("FIND_PHARMACIES.findPharmacies.searchForDrug")}
                            prefix={<Icon name="search" />}
                            shouldOpen={(currentValue) => {
                              return currentValue.length >= 3;
                            }}
                            dataSource={drugSearchDataSource}
                            onItemSelected={onDrugSelected}
                            testId="find-drug-pricing-field"
                            onChange={(newDrugName) => {
                              // clear any previous drop-down selection when input text changes
                              setSelectedDrug(undefined);
                              setDrugName(newDrugName);
                            }}
                            value={selectedDrug?.drug_name || drugName}
                          />
                        </Stack>
                      </Stack>
                    </Grid.Span>
                  )}
                </Grid>
                {selectedDrug && !hideDrugPricing && <DrugDetailsForm drug={selectedDrug} />}
              </Stack>
              <Shelf spacing="small">
                <ThemedSubmitButton testId="find-pharmacy-btn">{translate("FIND_PHARMACIES.findPharmacies.searchButton")}</ThemedSubmitButton>
                <ThemedButton appearance="secondary" onClick={onFormSearchCancel}>{translate("FIND_PHARMACIES.drugDetailsForm.clearButton")}</ThemedButton>
                <Shelf.Spacer />
              </Shelf>
            </Stack>
          </Card>
          <NeedHelp />
        </Stack>
      </Form>
    </PageLayout>
  );
};
